import { useCallback, useMemo, useState } from "react";
import {
  DEFAULT_MATERIAL,
  DEFAULT_SIZE,
  formatFtIn,
  getReducer,
  getSizeSpec,
  type MaterialId,
} from "@/lib/pipe-spec";

/** A point in 3D pipe space, in INCHES. x = east/right, y = north/left, z = up. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type AxisId = "x+" | "x-" | "y+" | "y-" | "z+" | "z-";

export const AXES: Record<AxisId, Vec3> = {
  "x+": { x: 1, y: 0, z: 0 },
  "x-": { x: -1, y: 0, z: 0 },
  "y+": { x: 0, y: 1, z: 0 },
  "y-": { x: 0, y: -1, z: 0 },
  "z+": { x: 0, y: 0, z: 1 },
  "z-": { x: 0, y: 0, z: -1 },
};

/** A continuous pipe run: a polyline of vertices. Turns become fittings. */
export interface PipeRun {
  id: string;
  material: MaterialId;
  /** size of the first segment (kept for labelling) */
  size: string;
  pts: Vec3[];
  /** size of each straight segment; length = pts.length - 1 */
  segSizes: string[];
  /** set when this run tees off an existing run */
  branch?: { hostRunId: string; at: Vec3; hostDir?: Vec3 | null };
}

export type FittingKind = "elbow90" | "elbow45" | "tee" | "reducer";

export interface RunFitting {
  runId: string;
  /** index of the vertex in run.pts */
  index: number;
  at: Vec3;
  kind: FittingKind;
  /** turn angle for elbows */
  angle: number;
  size: string;
  /** second size for reducers */
  sizeOut?: string;
  label: string;
  welds: number;
  /** for tees: screen-independent direction of the branch take-off */
  branchDir?: Vec3;
}

export interface CutPiece {
  runId: string;
  index: number;
  size: string;
  /** center-to-center length, inches */
  centerLength: number;
  /** actual cut length after fitting takeoffs, inches */
  cutLength: number;
  from: Vec3;
  to: Vec3;
  /** takeoff removed at each end, inches (used by the renderer too) */
  trimStart: number;
  trimEnd: number;
  weldEnds: number;
}

const eq = (a: Vec3, b: Vec3) => a.x === b.x && a.y === b.y && a.z === b.z;
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const len = (v: Vec3) => Math.hypot(v.x, v.y, v.z);
const norm = (v: Vec3): Vec3 => {
  const l = len(v) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
};
const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

export function useSketch() {
  const [material, setMaterial] = useState<MaterialId>(DEFAULT_MATERIAL);
  const [size, setSize] = useState<string>(DEFAULT_SIZE);
  const [runs, setRuns] = useState<PipeRun[]>([]);
  const [history, setHistory] = useState<PipeRun[][]>([]);
  const [future, setFuture] = useState<PipeRun[][]>([]);
  /** Run currently being drawn (id) */
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const commit = useCallback((next: PipeRun[]) => {
    setHistory((h) => [...h.slice(-49), runs]);
    setFuture([]);
    setRuns(next);
  }, [runs]);

  const activeRun = useMemo(
    () => runs.find((r) => r.id === activeRunId) ?? null,
    [runs, activeRunId],
  );

  const newId = () => `run-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  /** Start a new run at a point (or restart drawing). */
  const startRun = useCallback((p: Vec3) => {
    const id = newId();
    commit([...runs, { id, material, size, pts: [p], segSizes: [] }]);
    setActiveRunId(id);
  }, [commit, runs, material, size]);

  /**
   * Start a run that tees off an existing run at `p`.
   * The host segment is split so the tee lands on a real vertex.
   */
  const branchFrom = useCallback((hostRunId: string, p: Vec3) => {
    const host = runs.find((r) => r.id === hostRunId);
    if (!host) return;
    let nextHost = host;
    let hostDir: Vec3 | null = null;

    const vertexIndex = host.pts.findIndex((q) => eq(q, p));
    if (vertexIndex >= 0) {
      // locking onto an existing vertex: only interior vertices can carry a tee
      if (vertexIndex === 0 || vertexIndex === host.pts.length - 1) return;
      hostDir = norm(sub(host.pts[vertexIndex], host.pts[vertexIndex - 1]));
    } else {
      // find the segment containing p and split it
      let at = -1;
      for (let i = 0; i < host.pts.length - 1; i++) {
        const a = host.pts[i];
        const b = host.pts[i + 1];
        const ab = sub(b, a);
        const ap = sub(p, a);
        const t = dot(ap, ab) / (dot(ab, ab) || 1);
        if (t <= 0.001 || t >= 0.999) continue;
        const proj = {
          x: a.x + ab.x * t,
          y: a.y + ab.y * t,
          z: a.z + ab.z * t,
        };
        if (len(sub(proj, p)) < 0.6) {
          at = i;
          break;
        }
      }
      if (at < 0) return;
      hostDir = norm(sub(host.pts[at + 1], host.pts[at]));
      nextHost = {
        ...host,
        pts: [...host.pts.slice(0, at + 1), p, ...host.pts.slice(at + 1)],
        segSizes: [
          ...host.segSizes.slice(0, at + 1),
          host.segSizes[at],
          ...host.segSizes.slice(at + 1),
        ],
      };
    }

    const id = newId();
    commit([
      ...runs.map((r) => (r.id === hostRunId ? nextHost : r)),
      {
        id,
        material,
        size,
        pts: [p],
        segSizes: [],
        branch: { hostRunId, at: p, hostDir },
      },
    ]);
    setActiveRunId(id);
  }, [runs, commit, material, size]);


  /** Add the next vertex to the active run, using the currently selected size. */
  const extendRun = useCallback((p: Vec3) => {
    if (!activeRunId) return;
    const next = runs.map((r) => {
      if (r.id !== activeRunId) return r;
      const last = r.pts[r.pts.length - 1];
      if (eq(last, p)) return r;
      return { ...r, pts: [...r.pts, p], segSizes: [...r.segSizes, size] };
    });
    commit(next);
  }, [activeRunId, runs, commit, size]);

  /** Finish the run in progress. Drops single-point runs. */
  const finishRun = useCallback(() => {
    setRuns((rs) => rs.filter((r) => r.pts.length > 1));
    setActiveRunId(null);
  }, []);

  const removeLastVertex = useCallback(() => {
    if (!activeRunId) return;
    const next = runs
      .map((r) =>
        r.id === activeRunId
          ? { ...r, pts: r.pts.slice(0, -1), segSizes: r.segSizes.slice(0, -1) }
          : r,
      )
      .filter((r) => r.pts.length > 0);
    setRuns(next);
    if (!next.find((r) => r.id === activeRunId)) setActiveRunId(null);
  }, [activeRunId, runs]);

  const deleteRun = useCallback((id: string) => {
    commit(runs.filter((r) => r.id !== id && r.branch?.hostRunId !== id));
    if (activeRunId === id) setActiveRunId(null);
  }, [commit, runs, activeRunId]);

  const clearAll = useCallback(() => {
    commit([]);
    setActiveRunId(null);
  }, [commit]);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setFuture((f) => [runs, ...f]);
      setRuns(prev);
      setActiveRunId(null);
      return h.slice(0, -1);
    });
  }, [runs]);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      setHistory((h) => [...h, runs]);
      setRuns(f[0]);
      return f.slice(1);
    });
  }, [runs]);

  /**
   * Fittings implied by the geometry: direction changes -> elbows,
   * size changes -> concentric reducers, branch starts -> tees.
   * Also produces the takeoff consumed on each side of every vertex.
   */
  const derived = useMemo(() => {
    const fittings: RunFitting[] = [];
    /** runId -> per-vertex takeoff on the incoming / outgoing side */
    const trims = new Map<string, { in: number[]; out: number[] }>();

    for (const run of runs) {
      trims.set(run.id, {
        in: new Array(run.pts.length).fill(0),
        out: new Array(run.pts.length).fill(0),
      });
    }

    // tee lookups: host vertex -> branch direction (must be square to the host run)
    const teeAt = new Map<string, Vec3>();
    for (const run of runs) {
      if (!run.branch || run.pts.length < 2) continue;
      const dir = norm(sub(run.pts[1], run.pts[0]));
      const hd = run.branch.hostDir;
      if (hd && Math.abs(dot(dir, hd)) > 0.01) continue; // not a 90° take-off
      teeAt.set(`${run.branch.hostRunId}|${key(run.branch.at)}`, dir);
    }


    for (const run of runs) {
      const t = trims.get(run.id)!;

      // branch start: this run leaves a tee on its host
      if (run.branch && run.segSizes.length) {
        const bs = getSizeSpec(run.material, run.segSizes[0]);
        t.out[0] += bs.teeBranch;
      }

      for (let i = 1; i < run.pts.length - 1; i++) {
        const sizeIn = run.segSizes[i - 1];
        const sizeOut = run.segSizes[i];
        const specIn = getSizeSpec(run.material, sizeIn);
        const din = norm(sub(run.pts[i], run.pts[i - 1]));
        const dout = norm(sub(run.pts[i + 1], run.pts[i]));
        const cos = Math.max(-1, Math.min(1, dot(din, dout)));
        const turn = Math.round((Math.acos(cos) * 180) / Math.PI);

        const tee = teeAt.get(`${run.id}|${key(run.pts[i])}`);
        let placed = false;

        if (turn >= 1) {
          const is45 = turn <= 46;
          const takeoff = is45 ? specIn.elbow45 : specIn.elbow90;
          t.in[i] += takeoff;
          t.out[i] += takeoff;
          fittings.push({
            runId: run.id,
            index: i,
            at: run.pts[i],
            kind: is45 ? "elbow45" : "elbow90",
            angle: is45 ? 45 : 90,
            size: sizeIn,
            label: `${sizeIn} ${is45 ? 45 : 90}° LR Elbow BW`,
            welds: 2,
          });
          placed = true;
        }

        if (tee) {
          t.in[i] += specIn.teeRun;
          t.out[i] += specIn.teeRun;
          fittings.push({
            runId: run.id,
            index: i,
            at: run.pts[i],
            kind: "tee",
            angle: 90,
            size: sizeIn,
            label: `${sizeIn} Straight Tee BW`,
            welds: 3,
            branchDir: tee,
          });
          placed = true;
        }

        if (sizeIn !== sizeOut) {
          const red = getReducer(run.material, sizeIn, sizeOut);
          if (red) {
            // a reducer straddles the vertex unless a fitting is already there,
            // in which case it sits downstream of that fitting
            if (placed) {
              t.out[i] += red.length;
            } else {
              t.in[i] += red.length / 2;
              t.out[i] += red.length / 2;
            }
            fittings.push({
              runId: run.id,
              index: i,
              at: run.pts[i],
              kind: "reducer",
              angle: 0,
              size: sizeIn,
              sizeOut,
              label: red.label,
              welds: 2,
            });
          }
        }
      }
    }

    return { fittings, trims };
  }, [runs]);

  const fittings = derived.fittings;

  /** Straight pipe pieces with fitting takeoffs removed. */
  const cutList = useMemo<CutPiece[]>(() => {
    const out: CutPiece[] = [];
    for (const run of runs) {
      const t = derived.trims.get(run.id);
      if (!t) continue;
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const centerLength = len(sub(b, a));
        if (centerLength === 0) continue;
        const trimStart = t.out[i] ?? 0;
        const trimEnd = t.in[i + 1] ?? 0;
        const cut = centerLength - trimStart - trimEnd;
        out.push({
          runId: run.id,
          index: i,
          size: run.segSizes[i] ?? run.size,
          centerLength,
          cutLength: Math.max(0, cut),
          from: a,
          to: b,
          trimStart,
          trimEnd,
          weldEnds: (trimStart > 0 ? 1 : 0) + (trimEnd > 0 ? 1 : 0),
        });
      }
    }
    return out;
  }, [runs, derived]);

  const totals = useMemo(() => {
    const bySize = new Map<string, { feet: number; weight: number; pieces: number }>();
    let weight = 0;
    for (const p of cutList) {
      const spec = getSizeSpec(material, p.size);
      const ft = p.cutLength / 12;
      const w = ft * spec.lbPerFt;
      weight += w;
      const cur = bySize.get(p.size) ?? { feet: 0, weight: 0, pieces: 0 };
      cur.feet += ft;
      cur.weight += w;
      cur.pieces += 1;
      bySize.set(p.size, cur);
    }
    const fittingCounts = new Map<string, number>();
    for (const f of fittings) {
      fittingCounts.set(f.label, (fittingCounts.get(f.label) ?? 0) + 1);
    }
    const welds = fittings.reduce((s, f) => s + f.welds, 0);
    return {
      totalFeet: cutList.reduce((s, p) => s + p.cutLength / 12, 0),
      totalWeight: weight,
      bySize: [...bySize.entries()],
      fittingCounts: [...fittingCounts.entries()],
      welds,
      pieces: cutList.length,
    };
  }, [cutList, fittings, material]);

  return {
    material,
    setMaterial,
    size,
    setSize,
    runs,
    activeRun,
    activeRunId,
    startRun,
    branchFrom,
    extendRun,
    finishRun,
    removeLastVertex,
    deleteRun,
    clearAll,
    undo,
    redo,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    fittings,
    cutList,
    totals,
    formatFtIn,
  };
}

function key(p: Vec3) {
  return `${p.x},${p.y},${p.z}`;
}
